import React, { useState, useEffect, useCallback } from 'react';
import { UserCheck, UserX, Clock, Send, RefreshCw, XCircle, Copy, Check, Loader, Unlink } from 'lucide-react';
import { getEmployeeAccess, createInvitation, resendInvitation, cancelInvitation, unlinkEmployee } from '../../../api/invitations';
import { usePermissionsContext } from '../../../contexts/PermissionsContext';

const STATUS_CONFIG = {
  activo:    { icon: UserCheck, color: 'text-green-600',  bg: 'bg-green-50  border-green-200',  label: 'Usuario activo'        },
  pendiente: { icon: Clock,     color: 'text-amber-600',  bg: 'bg-amber-50  border-amber-200',  label: 'Invitación pendiente'  },
  expirada:  { icon: Clock,     color: 'text-gray-500',   bg: 'bg-gray-50   border-gray-200',   label: 'Invitación expirada'   },
  sin_acceso:{ icon: UserX,     color: 'text-red-500',    bg: 'bg-red-50    border-red-200',    label: 'Sin acceso al sistema' },
};

function CopyLink({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };
  return (
    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
      <p className="text-xs text-gray-500 mb-2 font-medium">Enlace de activación — compártelo con el empleado:</p>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={url}
          className="flex-1 text-xs bg-white border border-gray-200 rounded px-2 py-1.5 text-gray-600 font-mono truncate"
        />
        <button
          onClick={copy}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
            copied ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
          }`}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copiado' : 'Copiar'}
        </button>
      </div>
    </div>
  );
}

export default function EmployeeAccessSection({ employee }) {
  const { can } = usePermissionsContext();
  const canManage = can('USER_CREATE');

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');
  const [activationUrl, setActivationUrl] = useState('');

  const load = useCallback(() => {
    if (!employee?.id) return;
    setLoading(true);
    getEmployeeAccess(employee.id)
      .then(d => { setData(d); setError(''); })
      .catch(() => setError('No se pudo cargar el estado de acceso'))
      .finally(() => setLoading(false));
  }, [employee?.id]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async () => {
    setBusy(true);
    setError('');
    setActivationUrl('');
    try {
      const res = await createInvitation({ employeeId: employee.id });
      setActivationUrl(res.activationUrl);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al crear invitación');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (!data?.invitation?.id) return;
    setBusy(true);
    setError('');
    setActivationUrl('');
    try {
      const res = await resendInvitation(data.invitation.id);
      setActivationUrl(res.activationUrl);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al reenviar');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm('¿Desvincular usuario? El usuario quedará activo pero sin acceso hasta ser reactivado.')) return;
    setBusy(true);
    setError('');
    try {
      await unlinkEmployee(employee.id);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al desvincular');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!data?.invitation?.id) return;
    if (!window.confirm('¿Cancelar esta invitación?')) return;
    setBusy(true);
    setError('');
    try {
      await cancelInvitation(data.invitation.id);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cancelar');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-gray-400 text-sm">
        <Loader size={15} className="animate-spin" /> Cargando estado de acceso...
      </div>
    );
  }

  const status = data?.access_status || 'sin_acceso';
  const cfg    = STATUS_CONFIG[status] || STATUS_CONFIG.sin_acceso;
  const Icon   = cfg.icon;

  return (
    <div className={`rounded-xl border p-4 ${cfg.bg}`}>
      {/* Estado */}
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg bg-white/60`}>
          <Icon size={18} className={cfg.color} />
        </div>
        <div>
          <p className={`text-sm font-semibold ${cfg.color}`}>{cfg.label}</p>
          {status === 'activo' && data?.user && (
            <p className="text-xs text-gray-500">Usuario: <span className="font-mono font-medium">{data.user.username}</span></p>
          )}
          {(status === 'pendiente' || status === 'expirada') && data?.invitation && (
            <p className="text-xs text-gray-500">
              {status === 'pendiente'
                ? `Expira: ${new Date(data.invitation.expires_at).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                : 'El enlace ya expiró'
              }
            </p>
          )}
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
          {error}
        </p>
      )}

      {/* Desvincular — solo cuando activo */}
      {can('USER_EDIT') && status === 'activo' && (
        <div className="mt-2">
          <button
            onClick={handleUnlink}
            disabled={busy}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
          >
            {busy ? <Loader size={13} className="animate-spin" /> : <Unlink size={13} />}
            Desvincular usuario
          </button>
        </div>
      )}

      {/* Acciones */}
      {canManage && status !== 'activo' && (
        <div className="flex flex-wrap gap-2">
          {(status === 'sin_acceso' || status === 'expirada') && (
            <button
              onClick={handleInvite}
              disabled={busy || !employee?.email}
              title={!employee?.email ? 'El empleado no tiene email registrado' : ''}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {busy ? <Loader size={13} className="animate-spin" /> : <Send size={13} />}
              {status === 'expirada' ? 'Nueva invitación' : 'Invitar'}
            </button>
          )}

          {status === 'pendiente' && (
            <>
              <button
                onClick={handleResend}
                disabled={busy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                {busy ? <Loader size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                Reenviar
              </button>
              <button
                onClick={handleCancel}
                disabled={busy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                <XCircle size={13} /> Cancelar
              </button>
            </>
          )}

          {!employee?.email && (
            <p className="text-xs text-gray-500 italic">
              Agrega un email al empleado para poder invitarlo.
            </p>
          )}
        </div>
      )}

      {activationUrl && <CopyLink url={activationUrl} />}
    </div>
  );
}
